import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { QrCodeComponent } from '@warema/shared/components';
import {
    AppointmentRequestActivityType,
    Company,
    PrimeStatus,
    ShowroomClassification,
    WorkflowOrigin,
    WorkflowType,
} from '@warema/shared/models';
import { AppointmentTrackingService } from '@warema/shared/services';
import { SvgIconComponent } from '@warema/shared/svg';
import { WaremaButton, WaremaIcon } from '@warema/warema-ui-angular';
import { CompanyBadgesComponent } from '../company-badges/company-badges.component';
import { RatingStarsComponent } from '../rating-stars/rating-stars.component';

@Component({
    selector: 'warema-company-search-result',
    templateUrl: './company-search-result.component.html',
    styleUrls: ['./company-search-result.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslatePipe,
        RatingStarsComponent,
        CompanyBadgesComponent,
        QrCodeComponent,
        SvgIconComponent,
        WaremaIcon,
        WaremaButton,
    ],
})
export class CompanySearchResultComponent {
    private readonly _appointmentTrackingService = inject(AppointmentTrackingService);

    public readonly company = input.required<Company>();
    public readonly isPatioProductSearched = input<boolean>(false);
    public readonly isWindowProductSearched = input<boolean>(false);
    public readonly workflowType = input.required<WorkflowType>();
    public readonly showBadgesLCCEnabled = input<boolean>(false);
    public readonly businessCardUrlWithParameters = input<string>('');
    public readonly workflowOrigin = input.required<WorkflowOrigin>();
    public readonly activity = input<AppointmentRequestActivityType | null>(null);

    public readonly companySelected = output<string>();
    public readonly createAppointment = output<Company>();
    public readonly requestOffer = output<Company>();
    public readonly storeQueryForBusinessCard = output<void>();
    public readonly openBusinessCardPopup = output<string>();

    public readonly hasNoBadges = computed(() => {
        const company = this.company();
        return (
            company.showroom === ShowroomClassification.None &&
            company.primeStatus === PrimeStatus.None &&
            !company.providesOutdoorLiving &&
            !company.providesService
        );
    });

    public readonly workflowTypeEnum = WorkflowType;
    public readonly workflowOriginEnum = WorkflowOrigin;

    public readonly showQuoteRequestButton = computed(() => {
        const workflowType = this.workflowType();
        const workflowOrigin = this.workflowOrigin();
        const company = this.company();
        const isPatioProductSearched = this.isPatioProductSearched();
        const isWindowProductSearched = this.isWindowProductSearched();
        const activity = this.activity();

        if (workflowType === WorkflowType.Offer) {
            return true;
        }

        if (workflowType !== WorkflowType.CompanySearch && workflowType !== WorkflowType.Appointment) {
            return false;
        }

        if (workflowOrigin === WorkflowOrigin.Configurator) {
            return false;
        }

        if (isPatioProductSearched && !company.digitalBuyingAdvisorOdlUrl) {
            return false;
        }
        if (isWindowProductSearched && !company.digitalBuyingAdvisorWsyUrl && !company.digitalBuyingAdvisorWsyPlusUrl) {
            return false;
        }

        if (workflowOrigin === WorkflowOrigin.WaremaCom) {
            return true;
        }
        if (workflowOrigin === WorkflowOrigin.SunExperienceManager) {
            return true;
        }
        return activity === AppointmentRequestActivityType.Quotation;
    });

    public readonly showAppointmentButton = computed(
        () =>
            this.workflowType() !== WorkflowType.Offer && this.activity() !== AppointmentRequestActivityType.Quotation,
    );

    private _openBusinessCard(): void {
        const company = this.company();
        this._appointmentTrackingService.trackDealerInformation(company.name, company.addressId);

        if (this.workflowOrigin() === WorkflowOrigin.SunCenter) {
            this.openBusinessCardPopup.emit(company.addressId);
        } else {
            window.open(this.businessCardUrlWithParameters(), '_blank');
        }
    }

    public openFurtherDetails(): void {
        this.companySelected.emit(this.company().addressId);
        this.storeQueryForBusinessCard.emit();
        this._openBusinessCard();
    }
}
